import { useDeferredValue, useMemo } from 'react'

import { Search, X } from 'lucide-react'

import type { IssueSeverity } from '@/shared/constants/issue-status'

import { IssuesFilterDropdowns, type IssuesFilterOptions } from './IssuesFilterDropdowns'
import { exportIssuesReport, type ReportFilters } from '../../../api/reportsApi'
import { useHodDirectory } from '../../../hooks/useHodDirectory'
import { ageBandShort } from '../../../lib/dashboard-insights'
import { filterByFacets, type IssueFacets } from '../../../lib/issue-board'
import { GROUP_OPTIONS } from '../../../lib/issue-groups'
import { ISSUE_TYPE_CATALOG, issueTypeHeadingFor } from '../../../lib/issue-type-catalog'
import { viewCounts } from '../../../lib/issues'
import { prettyName } from '../../../lib/mandal-name'
import { useGisIssueView } from '../../../stores/gisIssueViewStore'
import { buildFacetOptions } from '../../ui/FilterDropdown'
import { GroupSelect } from '../../ui/GroupSelect'
import { SegmentedControl } from '../../ui/SegmentedControl'
import { FilterChipBar } from '../FilterChipBar'
import { ExportButton } from '../reports/ExportButton'

import type { HodDirectoryEntry } from '../../../api/hodApi'
import type { GisIssue, GisIssuesPage, GisScope } from '../../../api/types'

interface IssuesToolbarProps {
  scope: GisScope
  /** The issue-feed page (options + counts derive from its rows; undefined while cold-loading). */
  issuesPage: GisIssuesPage | undefined
  siteMandal: Map<string, string>
  activeMandals: readonly string[]
  onSelectMandal: (name: string) => void
  onClearMandal: () => void
  /** Entering Gallery closes any open detail drawer/overlay (the gallery uses its own modal). */
  onEnterGallery: () => void
}

/** HOD label for a department: the directory name, or a truthful fallback while it loads / for
 *  departments the directory doesn't know. */
const hodLabel = (department: string, directory: HodDirectoryEntry[] | undefined): string =>
  directory?.find((d) => d.department === department)?.hodName ?? `HOD — ${department}`

interface ChipSource {
  statusView: ReturnType<typeof useGisIssueView.getState>['statusView']
  severity: readonly IssueSeverity[]
  category: readonly string[]
  issueType: readonly string[]
  entity: readonly string[]
  department: readonly string[]
  hod: readonly string[]
  activeMandals: readonly string[]
  ageBands: readonly string[]
  siteNames: readonly string[]
}

/** One chip per applied filter value; each ✕ toggles just that value off. */
function buildChips(
  s: ChipSource,
  directory: HodDirectoryEntry[] | undefined,
  t: {
    setStatusView: (v: 'all' | 'open' | 'closed') => void
    toggleSeverity: (v: IssueSeverity) => void
    toggleCategory: (v: string) => void
    toggleIssueType: (v: string) => void
    toggleEntity: (v: string) => void
    toggleDepartment: (v: string) => void
    toggleHod: (v: string) => void
    toggleAgeBand: (v: string) => void
    toggleSiteName: (v: string) => void
    onSelectMandal: (v: string) => void
  },
): { label: string; clear: () => void }[] {
  const status: { label: string; clear: () => void }[] =
    s.statusView !== 'open'
      ? [{ label: `Status: ${s.statusView === 'all' ? 'All' : 'Closed'}`, clear: () => t.setStatusView('open') }]
      : []
  return [
    ...status,
    ...s.severity.map((v) => ({ label: `Importance: ${v}`, clear: () => t.toggleSeverity(v) })),
    ...s.category.map((v) => ({ label: `Type: ${v}`, clear: () => t.toggleCategory(v) })),
    ...s.issueType.map((v) => ({ label: `Issue type: ${v}`, clear: () => t.toggleIssueType(v) })),
    ...s.entity.map((v) => ({ label: `Entity: ${v}`, clear: () => t.toggleEntity(v) })),
    ...s.department.map((v) => ({ label: `Department: ${v}`, clear: () => t.toggleDepartment(v) })),
    ...s.hod.map((v) => ({ label: `HOD: ${hodLabel(v, directory)}`, clear: () => t.toggleHod(v) })),
    ...s.activeMandals.map((v) => ({ label: prettyName(v), clear: () => t.onSelectMandal(v) })),
    ...s.ageBands.map((v) => ({ label: `Age: ${ageBandShort(v)}`, clear: () => t.toggleAgeBand(v) })),
    ...s.siteNames.map((v) => ({ label: `Site: ${v}`, clear: () => t.toggleSiteName(v) })),
  ]
}

/**
 * The Issues page's full-width control row (V5 layout): free-text search · the all-facets Filters
 * popover · Group select · List/Gallery view toggle · Export. Replaces the old Explore Issues /
 * Explore By cards; every control writes `gisIssueViewStore` (mandal → the URL), so the map, the
 * chips row (second line, shown only when filters are applied) and the list stay in lockstep.
 */
export function IssuesToolbar({ scope, issuesPage, siteMandal, activeMandals, onSelectMandal, onClearMandal, onEnterGallery }: IssuesToolbarProps) {
  const feed = issuesPage?.issues ?? []
  const v = useGisIssueView
  const statusView = v((s) => s.statusView)
  const severity = v((s) => s.severity)
  const category = v((s) => s.category)
  const issueType = v((s) => s.issueType)
  const entity = v((s) => s.entity)
  const department = v((s) => s.department)
  const hod = v((s) => s.hod)
  const query = v((s) => s.query)
  const ageBands = v((s) => s.ageBands)
  const siteNames = v((s) => s.siteNames)
  const groupBy = v((s) => s.groupBy)
  const listView = v((s) => s.listView)
  const setStatusView = v((s) => s.setStatusView)
  const toggleSeverity = v((s) => s.toggleSeverity)
  const toggleCategory = v((s) => s.toggleCategory)
  const toggleIssueType = v((s) => s.toggleIssueType)
  const toggleEntity = v((s) => s.toggleEntity)
  const toggleDepartment = v((s) => s.toggleDepartment)
  const toggleHod = v((s) => s.toggleHod)
  const toggleAgeBand = v((s) => s.toggleAgeBand)
  const toggleSiteName = v((s) => s.toggleSiteName)
  const setQuery = v((s) => s.setQuery)
  const setGroupBy = v((s) => s.setGroupBy)
  const setListView = v((s) => s.setListView)
  const clearFacets = v((s) => s.clearFacets)

  const { data: hodDirectory } = useHodDirectory()

  // Match the list (IssuesDashboard): the dropdown counts + Export match-count filter on the
  // deferred query, so they settle on the same value the list shows instead of disagreeing per-keystroke.
  const deferredQuery = useDeferredValue(query)
  const facets: IssueFacets = { statusView, severity, category, issueType, entity, department, hod, query: deferredQuery, mandal: activeMandals, age: ageBands, site: siteNames }

  // Option lists + counts for the Filters popover. Each facet's counts exclude its OWN filter (so
  // its options stay switchable) but respect every other active filter — same pattern the old
  // severity donut used. The feed is small (one page), so the extra passes are cheap.
  const options: IssuesFilterOptions = useMemo(() => {
    const except = (patch: Partial<IssueFacets>): GisIssue[] =>
      filterByFacets(feed, { ...facets, ...patch }, siteMandal)
    const sevBase = except({ severity: [] })
    const sevCounts: Record<IssueSeverity, number> = { critical: 0, non_critical: 0 }
    for (const i of sevBase) {
      sevCounts[i.severity] += 1
    }
    const issueTypeHeadingCounts: Record<string, number> = {}
    for (const i of except({ issueType: [] })) {
      const heading = issueTypeHeadingFor(i.question)
      if (heading !== null) {
        issueTypeHeadingCounts[heading] = (issueTypeHeadingCounts[heading] ?? 0) + 1
      }
    }
    // Fixed list of the 6 curated headings (not derived from data presence, unlike
    // `buildFacetOptions`) — they're always offered, even at count 0.
    const issueTypeHeadings = ISSUE_TYPE_CATALOG.map((g) => ({
      value: g.heading,
      label: g.heading,
      count: issueTypeHeadingCounts[g.heading] ?? 0,
    }))
    return {
      status: viewCounts(except({ statusView: 'all' })),
      severity: sevCounts,
      issueTypeHeadings,
      entities: buildFacetOptions(feed, except({ entity: [] }), (i) => i.entityName),
      departments: buildFacetOptions(feed, except({ department: [] }), (i) => i.department),
      hods: buildFacetOptions(feed, except({ hod: [] }), (i) => i.department, (d) => hodLabel(d, hodDirectory)),
      mandals: buildFacetOptions(
        feed,
        except({ mandal: [] }),
        (i) => (i.siteId !== null ? (siteMandal.get(i.siteId) ?? null) : null),
        prettyName,
      ),
    }

  }, [feed, siteMandal, statusView, severity, category, issueType, entity, department, hod, deferredQuery, ageBands, siteNames, activeMandals, hodDirectory])

  const clearAll = (): void => {
    clearFacets()
    setStatusView('open')
    onClearMandal()
  }

  const chips = buildChips(
    { statusView, severity, category, issueType, entity, department, hod, activeMandals, ageBands, siteNames },
    hodDirectory,
    { setStatusView, toggleSeverity, toggleCategory, toggleIssueType, toggleEntity, toggleDepartment, toggleHod, toggleAgeBand, toggleSiteName, onSelectMandal },
  )

  // Open + Closed export via the server's `open`/`resolved` groups; the All view has no single
  // server group, so its export stays off. Scoped to the mandal only when exactly one is selected.
  const exportGroup: 'open' | 'resolved' | null =
    statusView === 'open' ? 'open' : statusView === 'closed' ? 'resolved' : null
  const exportFilters: ReportFilters = {
    scope,
    mandal: activeMandals.length === 1 ? activeMandals[0] : undefined,
  }
  const matchCount = useMemo(() => filterByFacets(feed, facets, siteMandal).length,

    [feed, siteMandal, statusView, severity, category, issueType, entity, department, hod, deferredQuery, ageBands, siteNames, activeMandals])

  const onViewChange = (next: 'list' | 'gallery'): void => {
    setListView(next)
    if (next === 'gallery') {
      onEnterGallery()
    }
  }

  return (
    <div className="border-b border-gis-line bg-white">
      {/* LEFT: search + one dropdown per filter (wraps on narrow widths). RIGHT: Group · view · Export. */}
      <div className="flex flex-wrap items-center gap-2 px-3.5 py-[7px]">
        {/* Search — bounded width; ✕ clears. Matches title/ref/site/entity/department/type. */}
        <label className="flex h-[30px] w-[220px] min-w-[170px] items-center gap-[7px] rounded-[9px] border border-gis-line bg-white px-[10px] transition-colors focus-within:border-[#c7d2ea] focus-within:bg-[#fbfcfe]">
          <Search className="h-[13px] w-[13px] flex-none text-gis-muted" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search issues…"
            aria-label="Search issues"
            className="min-w-0 flex-1 bg-transparent text-[12px] text-gis-ink outline-none placeholder:text-gis-muted [&::-webkit-search-cancel-button]:hidden"
          />
          {query !== '' && (
            <button type="button" aria-label="Clear search" onClick={() => setQuery('')}
              className="grid h-4 w-4 flex-none place-items-center rounded-full text-gis-muted transition-colors hover:bg-gis-track hover:text-gis-ink">
              <X className="h-[11px] w-[11px]" />
            </button>
          )}
        </label>

        <IssuesFilterDropdowns
          options={options}
          activeMandals={activeMandals}
          onToggleMandal={onSelectMandal}
        />

        <div className="ml-auto flex flex-none items-center gap-2">
          <GroupSelect options={GROUP_OPTIONS} value={groupBy} noneValue="none" onChange={setGroupBy} />
          <SegmentedControl<'list' | 'gallery'>
            aria-label="Gallery or list view"
            size="sm"
            track="a"
            shape="rounded"
            // Gallery first — it is the default presentation.
            items={[{ key: 'gallery', label: 'Gallery' }, { key: 'list', label: 'List' }]}
            value={listView}
            onChange={onViewChange}
          />
          <ExportButton
            onExport={() =>
              exportGroup
                ? exportIssuesReport(
                    exportFilters,
                    exportGroup,
                    // The xlsx export takes a single severity; pass it only when exactly one is
                    // selected — multiple selected severities export as "all" (no severity param).
                    severity.length === 1 ? severity[0] : undefined,
                  )
                : Promise.resolve()
            }
            disabled={matchCount === 0 || exportGroup === null}
          />
        </div>
      </div>

      {chips.length > 0 && (
        <div className="border-t border-gis-line-2 px-3.5 py-[5px]">
          <FilterChipBar chips={chips} onClearAll={clearAll} />
        </div>
      )}
    </div>
  )
}
